/**
 * Conversation Sidebar Component
 * Multi-Chat System: Sidebar for managing conversations
 */

import React, { useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Fade,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Forum as ForumIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useDialogState } from '@/hooks/ui/useDialogState';
import { log } from '../../utils/errors/logger';
import {
  useGetConversations,
  useCreateConversation,
  useDeleteConversation,
  useUpdateConversation,
} from '../../hooks/api/useConversations';
import { useConversationStore } from '../../stores/conversationStore';
import { ConversationManagementDialog } from './ConversationManagementDialog';
import { ConversationList } from './ConversationList';
import { DeleteConfirmationDialog } from '../ui/DeleteConfirmationDialog';

interface ConversationSidebarProps {
  open: boolean;
  onClose: () => void;
}

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const dialogState = useDialogState();
  const navigate = useNavigate();

  // Get current conversation ID from URL
  const { conversationId: currentConversationId } = useParams<{ conversationId?: string }>();

  // API hooks
  const { data: conversations = [], isLoading, error } = useGetConversations();
  const createConversationMutation = useCreateConversation();
  const deleteConversationMutation = useDeleteConversation();
  const updateConversationMutation = useUpdateConversation();

  // Event handlers
  const handleCreateConversation = async () => {
    try {
      log.user.action('new_conversation_click', 'ConversationSidebar');

      const newConversation = await createConversationMutation.mutateAsync(undefined);

      // Navigate to the new conversation
      navigate(`/chat/${newConversation.id}`);

      showSuccess(t('conversation.created', { title: newConversation.title }));

      log.user.action('conversation_created_success', 'ConversationSidebar', {
        conversationId: newConversation.id,
        title: newConversation.title,
      });
    } catch (error) {
      log.component.error('ConversationSidebar', { error: 'Failed to create conversation' });
      showError(t('conversation.createFailed'));
    }
  };

  const handleSelectConversation = (conversationId: string) => {
    if (conversationId !== currentConversationId) {
      // Navigate to the selected conversation
      navigate(`/chat/${conversationId}`);
      log.user.action('conversation_selected', 'ConversationSidebar', { conversationId });
    }

    // Close sidebar on mobile after selection
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  const handleDeleteConversation = (conversationId: string, title: string) => {
    dialogState.openDeleteConfirm({ id: conversationId, title } as any);
  };

  const handleConfirmDelete = async () => {
    if (!dialogState.itemToDelete) {return;}

    const { id: conversationId, title } = dialogState.itemToDelete;

    try {
      await deleteConversationMutation.mutateAsync(conversationId);

      // If we deleted the current conversation, navigate to chat home
      if (conversationId === currentConversationId) {
        navigate('/chat', { replace: true });
      }

      showSuccess(t('conversation.deleted', { title }));
      dialogState.closeDeleteConfirm();

      log.user.action('conversation_deleted_success', 'ConversationSidebar', {
        conversationId,
        title,
      });
    } catch (error) {
      log.component.error('ConversationSidebar', { error: 'Failed to delete conversation' });
      showError(t('conversation.deleteFailed'));
    }
  };

  const handleEditConversation = (conversation: any) => {
    dialogState.openEditDialog(conversation);
  };

  const handleDialogSubmit = async (data: any) => {
    if (dialogState.isEditing && dialogState.editingItem) {
      try {
        await updateConversationMutation.mutateAsync({
          conversationId: dialogState.editingItem.id,
          updates: { title: data.title },
        });

        showSuccess(t('conversation.updated'));
        dialogState.closeDialog();

        log.user.action('conversation_updated_success', 'ConversationSidebar', {
          conversationId: dialogState.editingItem.id,
          newTitle: data.title,
        });
      } catch (error) {
        log.component.error('ConversationSidebar', { error: 'Failed to update conversation' });
        showError(t('conversation.updateFailed'));
      }
    }
  };

  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;

      // N - New conversation
      if ((e.key === 'n' || e.key === 'N') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const target = e.target as HTMLElement;
        // Don't trigger if user is typing in an input
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          handleCreateConversation();
        }
      }

      // Escape - Close sidebar
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [open, onClose]
  );

  // Setup keyboard shortcuts
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Component logging
  useEffect(() => {
    if (open) {
      log.component.mount('ConversationSidebar', { conversationCount: conversations.length });
    }
  }, [open, conversations.length]);

  return (
    <Fade in={open} timeout={250}>
      <Box
        role="complementary"
        aria-label={t('conversation.sidebar')}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          boxShadow: 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ForumIcon color="primary" sx={{ fontSize: 28 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {t('conversation.conversations')}
              </Typography>
            </Box>

            <Tooltip title={`${t('common.close')} (Esc)`} arrow>
              <IconButton
                onClick={onClose}
                aria-label={t('common.close')}
                sx={{
                  width: 40,
                  height: 40,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'rotate(90deg)',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* New conversation button */}
          <Tooltip title={`${t('conversation.new')} (N)`} arrow placement="bottom">
            <Button
              variant="contained"
              fullWidth
              startIcon={<AddIcon />}
              onClick={handleCreateConversation}
              disabled={createConversationMutation.isPending}
              sx={{
                py: 1.25,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              {t('conversation.new')}
            </Button>
          </Tooltip>
        </Box>

        <Divider />

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ConversationList
            conversations={conversations}
            currentConversationId={currentConversationId}
            isLoading={isLoading}
            error={error}
            onSelect={handleSelectConversation}
            onDelete={handleDeleteConversation}
            onEdit={handleEditConversation}
          />
        </Box>

        {/* Footer hint */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <kbd style={{
              padding: '2px 6px',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'currentColor',
              fontSize: '0.75rem',
              fontFamily: 'monospace'
            }}>
              N
            </kbd>
            {t('conversation.newShortcut')} •
            <kbd style={{
              padding: '2px 6px',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'currentColor',
              fontSize: '0.75rem',
              fontFamily: 'monospace'
            }}>
              Esc
            </kbd>
            {t('common.close')}
          </Typography>
        </Box>

        {/* Management Dialog */}
        <ConversationManagementDialog
          open={dialogState.dialogOpen}
          onClose={dialogState.closeDialog}
          conversation={dialogState.editingItem}
          mode="edit"
          onSubmit={handleDialogSubmit}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          open={dialogState.deleteConfirmOpen}
          onClose={dialogState.closeDeleteConfirm}
          onConfirm={handleConfirmDelete}
          itemName={dialogState.itemToDelete?.title}
        />
      </Box>
    </Fade>
  );
};